// useChannelThreadMessages.js

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

// Channels
import {
  THREAD_REPLY_PAGE_SIZE,
  subscribeToThreadMessages,
} from 'channels/services/ChannelReadService';
import {
  deleteChannelMessage,
  editChannelMessage,
  postChannelThreadReply,
} from 'channels/services/ChannelWriteService';

// Utilities
import { generateKey } from 'utilities/Keys';
import { getPersonName, getPersonPhoto } from 'utilities/PersonIdentity';
import { buildUserSummary } from 'utilities/Users';

/**
 * useChannelThreadMessages
 *
 * Subscribes to one thread's replies and exposes reply mutations.
 *
 * @param {object} options - Hook options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.threadRootKey - Thread root message key.
 * @param {object} options.user - Active user.
 * @param {boolean} options.enabled - Whether subscription is active.
 *
 * @returns {object} Thread reply state and action handlers.
 */
const useChannelThreadMessages = ({
  baseKey,
  channelKey,
  threadRootKey,
  user,
  enabled = true,
} = {}) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [windowSize, setWindowSize] = useState(THREAD_REPLY_PAGE_SIZE);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);

  const normalizeNodeReferences = useCallback((nodeReferences = []) => {
    const seen = new Set();

    return (Array.isArray(nodeReferences) ? nodeReferences : [])
      .map((row) => {
        const nodeKey = String(row?.nodeKey || row?.key || '').trim();
        if (!nodeKey) return null;
        if (seen.has(nodeKey)) return null;
        seen.add(nodeKey);

        return {
          key: generateKey(),
          type: 'node_ref',
          nodeKey,
          title: String(row?.title || '').trim(),
          nodeType: String(row?.nodeType || row?.type || '').trim() || 'NODE',
        };
      })
      .filter(Boolean);
  }, []);

  useLayoutEffect(() => {
    const cleanRootKey = String(threadRootKey || '').trim();
    if (!enabled || !baseKey || !channelKey || !cleanRootKey) return;
    setLoading(true);
  }, [channelKey, enabled, threadRootKey, baseKey]);

  // A different thread starts from the first page again.
  useEffect(() => {
    setWindowSize(THREAD_REPLY_PAGE_SIZE);
    setHasMoreReplies(false);
  }, [channelKey, threadRootKey, baseKey]);

  useEffect(() => {
    const cleanRootKey = String(threadRootKey || '').trim();
    if (!enabled || !baseKey || !channelKey || !cleanRootKey) {
      setReplies([]);
      setLoading(false);
      setErrorMessage('');
      return () => {};
    }

    setLoading(true);
    setErrorMessage('');

    const unsubscribe = subscribeToThreadMessages(
      baseKey,
      channelKey,
      cleanRootKey,
      (rows, meta) => {
        setReplies(Array.isArray(rows) ? rows : []);
        setHasMoreReplies(Boolean(meta?.hasMore));
        setLoading(false);
      },
      (error) => {
        setErrorMessage(error?.message || 'Failed to load thread replies.');
        setLoading(false);
      },
      { windowSize }
    );

    return () => unsubscribe && unsubscribe();
  }, [channelKey, enabled, threadRootKey, baseKey, windowSize]);

  const loadMoreReplies = useCallback(() => {
    if (!hasMoreReplies) return;
    setWindowSize((current) => current + THREAD_REPLY_PAGE_SIZE);
  }, [hasMoreReplies]);

  const sendReply = useCallback(async ({ text, nodeReferences = [], mentionKeys = [] } = {}) => {
    const cleanRootKey = String(threadRootKey || '').trim();
    if (!baseKey || !channelKey || !cleanRootKey || !user?.key) {
      return { ok: false, messageKey: '' };
    }

    const cleanText = String(text || '').trim();
    const nodeReferenceAttachments = normalizeNodeReferences(nodeReferences);
    const cleanMentionKeys = Array.from(new Set(
      (Array.isArray(mentionKeys) ? mentionKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    ));

    if (!cleanText && nodeReferenceAttachments.length === 0) {
      return { ok: false, messageKey: '' };
    }

    setErrorMessage('');
    setIsSending(true);

    try {
      const userSummary = buildUserSummary(user);

      const created = await postChannelThreadReply({
        baseKey,
        channelKey,
        threadRootKey: cleanRootKey,
        userKey: user.key,
        user: userSummary,
        userName: userSummary?.name || getPersonName(user, 'Member'),
        userPhoto: userSummary?.coverUrl || getPersonPhoto(user, ''),
        text: cleanText,
        attachments: nodeReferenceAttachments,
        mentionKeys: cleanMentionKeys,
      });

      const messageKey = String(created?.key || '').trim();
      if (!messageKey) {
        return { ok: false, messageKey: '' };
      }

      return { ok: true, messageKey };
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to send reply.');
      return { ok: false, messageKey: '' };
    } finally {
      setIsSending(false);
    }
  }, [channelKey, normalizeNodeReferences, threadRootKey, user, baseKey]);

  const updateMessage = useCallback(async ({ messageKey, text }) => {
    if (!baseKey || !channelKey || !messageKey) return false;

    try {
      await editChannelMessage({
        baseKey,
        channelKey,
        messageKey,
        text,
      });
      return true;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to edit reply.');
      return false;
    }
  }, [baseKey, channelKey]);

  const removeMessage = useCallback(async ({ messageKey }) => {
    if (!baseKey || !channelKey || !messageKey || !user?.key) return false;

    try {
      await deleteChannelMessage({
        baseKey,
        channelKey,
        messageKey,
        userKey: user.key,
      });
      return true;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to delete reply.');
      return false;
    }
  }, [baseKey, channelKey, user?.key]);

  return {
    replies,
    loading,
    errorMessage,
    isSending,
    hasMoreReplies,
    loadMoreReplies,
    sendReply,
    updateMessage,
    removeMessage,
  };
};

export default useChannelThreadMessages;
